import { Buffer } from "buffer";
import { LIGHTHOUSE_PROGRAM, validLighthouseAssertion } from "./lighthouse.js";
import { COMPUTE_BUDGET_PROGRAM, withinPreparedComputeBudget } from "./computeBudget.js";
export class WalletPolicyError extends Error {
    reason;
    addedProgramIds;
    code = "wallet_approval_incompatible";
    status = 409;
    retryable = false;
    constructor(reason, addedProgramIds = []) {
        super("Your wallet returned an approval we could not validate. Refresh to check its status before trying again.");
        this.reason = reason;
        this.addedProgramIds = addedProgramIds;
    }
}
export function assertWalletMessage(before, after, tables = []) {
    const programs = (message) => new Set(message.compiledInstructions.map(ix => message.staticAccountKeys[ix.programIdIndex]?.toBase58()).filter((id) => !!id));
    const originalPrograms = programs(before);
    let addedPrograms = [...programs(after)].filter(id => !originalPrograms.has(id));
    const reject = (reason) => { throw new WalletPolicyError(reason, addedPrograms); };
    if (before.version !== after.version || before.recentBlockhash !== after.recentBlockhash)
        reject("version_or_blockhash");
    const resolve = (message) => {
        try {
            const keys = message.getAccountKeys({ addressLookupTableAccounts: [...tables] });
            return Array.from({ length: keys.length }, (_, i) => keys.get(i));
        }
        catch {
            return reject("lookup_unavailable");
        }
    };
    const a = resolve(before), b = resolve(after);
    if (!a[0] || a[0].toBase58() !== b[0]?.toBase58())
        reject("payer");
    const indexes = (keys) => new Map(keys.map((key, i) => [key.toBase58(), i]));
    const ai = indexes(a), bi = indexes(b);
    if (ai.size !== a.length || bi.size !== b.length)
        reject("duplicate_accounts");
    for (const [key, i] of ai) {
        const j = bi.get(key);
        if (j === undefined || before.isAccountSigner(i) !== after.isAccountSigner(j) || before.isAccountWritable(i) !== after.isAccountWritable(j))
            reject("permissions");
    }
    const normalize = (message, keys) => message.compiledInstructions.map(ix => ({
        program: keys[ix.programIdIndex]?.toBase58(), accounts: ix.accountKeyIndexes.map(i => keys[i]?.toBase58()), data: Buffer.from(ix.data).toString("hex"),
    }));
    let original = normalize(before, a), returned = normalize(after, b);
    addedPrograms = [...new Set(returned.map(ix => ix.program).filter(program => !original.some(ix => ix.program === program)))];
    // Supported assertions may use the bounded shared compute/fee reserve.
    // Changed co-signed messages need fresh authority signatures after validation.
    // Nonce messages stay exact. Instruction-index-sensitive messages must keep
    // every original instruction (including each budget opcode) in its position.
    const indexed = ai.has("Sysvar1nstructions1111111111111111111111111");
    const nonce = original.some(ix => ix.program === "11111111111111111111111111111111" && ix.data === "04000000");
    const validAssertions = after.compiledInstructions.filter((ix, i) => returned[i].program === LIGHTHOUSE_PROGRAM
        && validLighthouseAssertion(ix.data, ix.accountKeyIndexes.length)).length;
    const walletBudget = !nonce
        && addedPrograms.includes(LIGHTHOUSE_PROGRAM) && withinPreparedComputeBudget(before, after, validAssertions);
    const flexibleBudget = walletBudget && !indexed;
    const returnedInstructions = after.compiledInstructions.filter((_ix, i) => !flexibleBudget || returned[i].program !== COMPUTE_BUDGET_PROGRAM);
    if (flexibleBudget) {
        original = original.filter(ix => ix.program !== COMPUTE_BUDGET_PROGRAM);
        returned = returned.filter(ix => ix.program !== COMPUTE_BUDGET_PROGRAM);
    }
    else if (walletBudget) {
        // The budget parser already checked both complete payloads and their fee
        // ceiling. Ignore only those values, retaining opcode, accounts and index.
        const budgetOpcode = (ix) => ix.program === COMPUTE_BUDGET_PROGRAM
            ? { ...ix, data: ix.data.slice(0, 2) } : ix;
        original = original.map(budgetOpcode);
        returned = returned.map(budgetOpcode);
    }
    let cursor = 0, assertions = 0;
    const assertionKeys = new Set();
    // Instructions-sysvar consumers may bind absolute indexes; appended guards
    // cannot shift an existing proof or its consumer.
    for (let i = 0; i < returned.length; i++) {
        if (JSON.stringify(returned[i]) === JSON.stringify(original[cursor])) {
            cursor++;
            continue;
        }
        const ix = returnedInstructions[i];
        if (returned[i].program !== LIGHTHOUSE_PROGRAM || indexed && cursor < original.length
            || ++assertions > 16 || !validLighthouseAssertion(ix.data, ix.accountKeyIndexes.length))
            reject("unsupported_instruction");
        assertionKeys.add(LIGHTHOUSE_PROGRAM);
        for (const account of returned[i].accounts) {
            if (!account)
                reject("account_index");
            assertionKeys.add(account);
        }
    }
    if (cursor !== original.length)
        reject("business_instructions");
    for (const [key, i] of bi)
        if (!ai.has(key) && (!assertionKeys.has(key) || after.isAccountWritable(i) || after.isAccountSigner(i)))
            reject("added_permissions");
    if (before.header.numRequiredSignatures !== after.header.numRequiredSignatures)
        reject("signers");
}
